import { useState, type FormEvent } from "react";
import { useKeepLabourLine, useRemoveLabourLine, type BoqDetail, type DoubleLabourLine, type SupplierLabour } from "../../api/boq";

function carrier(supplier: SupplierLabour): string {
  return supplier.source === "quotation"
    ? `Approved quotation ${supplier.quotation?.number ?? ""}`
    : `The ${supplier.kit_size_label ?? ""} service kit`;
}

/** Labour on the bill: where B15's hours came from (the labour allowance for
 * the valve's make and DN, and the minimum charge), and any rate-card line
 * billing labour the supplier's K1 already carries. Such a line holds the bill
 * back from the City until it is removed, or kept with a reason the override
 * register keeps. */
export default function LabourPanel({ detail }: { detail: BoqDetail }) {
  const { b15, supplier_labour: supplier, double_labour: lines } = detail.labour;
  if (b15 === null && lines.length === 0) {
    return null;
  }
  return (
    <section className="cm-block" aria-label="Labour on this bill">
      <h3>Labour</h3>
      {b15?.sentence ? <p className="cm-doc-note">{b15.sentence}</p> : null}
      {supplier !== null && lines.length > 0 ? (
        <p className="cm-doc-note">
          {carrier(supplier)} already carries the supplier's labour inside K1: {supplier.lines.join("; ")}.
        </p>
      ) : null}
      {lines.map((line) => (
        <LabourLine key={line.item_id} boqId={detail.id} line={line} canResolve={detail.actions.can_resolve_labour} />
      ))}
    </section>
  );
}

function LabourLine({ boqId, line, canResolve }: { boqId: number; line: DoubleLabourLine; canResolve: boolean }) {
  const keep = useKeepLabourLine(boqId);
  const remove = useRemoveLabourLine(boqId);
  const [reason, setReason] = useState("");
  if (line.kept) {
    return (
      <p className="notice">
        {line.payment_ref} is kept beside the supplier's labour: {line.kept_reason ?? "—"}
        {line.kept_by ? ` (${line.kept_by})` : ""}.
      </p>
    );
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    keep.mutate({ itemId: line.item_id, reason: reason.trim() }, { onSuccess: () => setReason("") });
  }

  return (
    <form className="cm-form" aria-label={`Resolve ${line.payment_ref}`} onSubmit={onSubmit}>
      <p className="notice warn">
        {line.payment_ref} {line.description} bills labour the supplier already charges inside K1.
      </p>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={!canResolve || remove.isPending}
        onClick={() => remove.mutate(line.item_id)}
      >
        Remove
      </button>
      <label className="move-note">
        Why {line.payment_ref} stays (kept in the override register)
        <textarea value={reason} rows={2} onChange={(event) => setReason(event.target.value)} placeholder="Required" />
      </label>
      <button type="submit" className="btn btn-primary" disabled={!canResolve || keep.isPending || reason.trim() === ""}>
        Keep, with a reason
      </button>
      {keep.isError ? <p className="notice bad">{keep.error.message}</p> : null}
      {remove.isError ? <p className="notice bad">{remove.error.message}</p> : null}
    </form>
  );
}
